import { cn, createBEM } from "@/libs";
import { ComponentBase } from "@/types";
import { Button, Dialog, DialogShowProps } from "antd-mobile";
import { ExclamationCircleFill } from "antd-mobile-icons";
import { Action } from "antd-mobile/es/components/dialog";
import { ReactNode } from "react";
import "./index.scss";
interface DialogLocalProps extends DialogShowProps, ComponentBase.Component {
  title?: string;
  theme?: "normal" | "warning";
  content?: ReactNode;
  cancelText?: ReactNode;
  confirmText?: ReactNode;
  onCancel?: () => void | Promise<boolean>;
  onConfirm?: () => void | Promise<boolean>;
  confirmTextColor?: string;
  cancelTextColor?: string;
}
const b = createBEM("i-dialog");
/**
 * Dialog 指令式调用无法受控于 state 状态更新
 */
/* `export const DialogLocal` 正在导出一个名为 `DialogLocal` 的函数，该函数接受一个 props 对象并返回一个解析为 Dialog 组件的
Promise。该函数使用 `antd-mobile` 库中的 `Dialog` 组件来创建一个具有可自定义标题、内容和按钮的对话框。该函数还允许在用户单击取消或确认按钮时传入可选的回调。 */
export const DialogLocal = async ({
  className,
  title = "提示",
  theme = "normal",
  content,
  onConfirm,
  onCancel,
  cancelText,
  confirmText = "确认",
  confirmTextColor,
  cancelTextColor,
  actions,
  ...rest
}: DialogLocalProps) => {
  const action: Action[] = [{
    className: cn(b("button", ["confirm"])),
    key: "confirm",
    onClick: async () => {
      if (onConfirm) {
        let shouldCloseDialog;
        const mayPromise: any = onConfirm();
        if (mayPromise instanceof Promise) {
          shouldCloseDialog = await mayPromise;
          shouldCloseDialog && handler.close();
        } else {
          handler.close();
        }
        return;
      }
      handler.close();
      return;
    },
    text: confirmText,
    style: {
      color: confirmTextColor
    }
  }];
  if (cancelText) {
    action.push({
      className: cn(b("button", ["cancel"])),
      key: "cancel",
      onClick: async () => {
        if (onCancel) {
          let shouldCloseDialog;
          const mayPromise: any = onCancel();
          if (mayPromise instanceof Promise) {
            shouldCloseDialog = await mayPromise;
            shouldCloseDialog && handler.close();
          } else {
            handler.close();
          }
          return;
        }
        handler.close();
        return;
      },
      text: cancelText,
      style: {
        color: cancelTextColor
      }
    });
  }
  const handler = await Dialog.show({
    bodyClassName: cn(b([theme]), className),
    maskClassName: cn(b("mask")),
    content: <div className={b("body")}>
        {theme === "warning" && <ExclamationCircleFill className={cn(b("icon", ["warning"]))} />}
        {title && <div className={b("title")}>{title}</div>}
        {content && <div className={b("content")}>{content}</div>}
      </div>,
    actions: actions || [action],
    ...rest
  });
  return handler;
};
export interface MakeDialogProps extends DialogShowProps, ComponentBase.Component {
  title: string;
  theme?: "normal" | "warning";
  content?: ReactNode;
  cancelText?: ReactNode;
  confirmText?: ReactNode;
  onCancel?: () => void;
  onConfirm?: () => void;
  confirmTextColor?: string;
  cancelTextColor?: string;
  disabledConfirmButton?: boolean;
  disabledCancelButton?: boolean;
}
export const MakeDialog = ({
  title,
  content,
  onConfirm,
  onCancel,
  cancelText,
  confirmText = "确认",
  confirmTextColor,
  cancelTextColor,
  disabledConfirmButton = false,
  disabledCancelButton = false
}: MakeDialogProps) => {
  const action: Action[] = [{
    className: cn(b("button", ["confirm"])),
    key: "confirm",
    onClick: async () => {
      onConfirm && onConfirm();
    },
    text: confirmText,
    style: {
      color: confirmTextColor
    },
    disabled: disabledConfirmButton
  }];
  if (cancelText) {
    action.push({
      className: cn(b("button", ["cancel"])),
      key: "cancel",
      onClick: () => {
        onCancel && onCancel();
      },
      text: cancelText,
      style: {
        color: cancelTextColor
      },
      disabled: disabledCancelButton
    });
  }
  return <div className={b()} data-sentry-component="MakeDialog" data-sentry-source-file="index.tsx">
      <div className={b("body")}>
        {title && <div className={b("title")}>{title}</div>}
        {content && <div className={b("content")}>{content}</div>}
      </div>
      <div className={b("footer")}>
        {action.map(item => <Button {...{
        ...item,
        text: undefined
      }}>{item.text}</Button>)}
      </div>
    </div>;
};